import React, { useEffect, useRef } from 'react';
import './App.scss';
import './brand.scss';
import { Route, Switch, useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import 'url-search-params-polyfill';
import { useDispatch } from 'react-redux';

import config from 'config';

import { clearVideoIsClicked } from 'actions/clickedVideo';

import AppActionsHandler from 'components/support/AppActionsHandler';
import LoggerOverlay from 'components/support/LoggerOverlay';
import AuthProvider from 'components/providers/AuthProvider';
import { FeatureFlagProvider } from 'components/providers/FeatureFlagProvider';
import AriaTextToSpeech from 'components/support/AriaTextToSpeech';

import { useNextVideo, usePrevVideo, useFallbackVideo } from 'hooks/video';

import textToSpeechManager from 'support/tts/TextToSpeechManager';

import PrivacyManager from 'support/privacy/PrivacyManager';

// In order to support code splitting and dynamically loading bundles, I've
// included examples of how to do this below.  There are three different
// approaches to setting up your application:

// 1. Static imports (this generates a single code bundle for your application
// that loads all at once on app startup)
import Home from 'components/pages/home/Home';
import SplashWrapper from 'components/SplashWrapper';
import UpcomingEventPage from 'components/pages/UpcomingEvent';
import UpgradeToWatch from 'components/pages/playback/UpgradeToWatch';
import ShortCode from 'components/pages/shortCode/ShortCode';
import Logout from 'components/pages/Logout';
import PlaybackPage from 'components/playback/PlaybackPage';
import { PlayerStateProvider } from 'components/providers/PlayerStateContext';
import ExitDialog from 'components/pages/ExitDialog';
import LegalNoticesPage from 'components/pages/legalNotices/LegalNotices';
import ClosedCaptionsPage from 'components/pages/closedCaptions/ClosedCaptions';
import ErrorPage from 'components/pages/Error';
import { FormattedMessage } from 'react-intl';
import SyncbakErrorPage from 'components/error/SyncbakError';
import NetworkErrorPage from 'components/error/NetworkError';
import KitchenSinkFetcher from 'components/KitchenSinkFetcher';
import FMSFetcher from 'components/FMSFetcher';
import ContactUsPage from 'components/pages/contactUs/ContactUs';
import InteriorPage from 'components/pages/home/InteriorPage';
import SettingsPage from 'components/pages/home/SettingsPage';
import LandingPage from 'components/pages/landing/LandingPage';
import PrivacyPageUS from 'components/pages/privacy/PrivacyPageUS';
import PrivacyPageGDPR from 'components/pages/privacy/PrivacyPageGDPR';
import GraphQLPocRoutes from 'components/pages/graphqlPoc/GraphQLPocRoutes';
import { MemoryHistory } from 'history';

// 2. Dynamic Imports w/o pre-fetching (this generates multiple code bundles;
// one per route).  Initial startup will only fetch the page that's referenced
// at startup (ie. the one that lives under the root path "/").  All other
// pages will then be fetched when they're rendered
/*
const Home = React.lazy(() => import('components/pages/Home'));
*/

// 3. Dynamic Imports w/ pre-fetching.  Similar to the option above, but
// pre-fetches all other page bundles on app startup as well.  Key difference
// from static imports is that the application does not wait for those other
// page bundles to load before starting up.
/*
const homePromise = import('components/pages/Home');
const Home = React.lazy(() => homePromise);
*/

const App = () => {
  const history = useHistory() as MemoryHistory;
  const dispatch = useDispatch();
  PrivacyManager.initializeCMP();

  useEffect(() => {
    const {
      brand: { name },
    } = config;
    if (document) {
      document.title = name;
    }
  }, []);

  useEffect(() => {
    const stopTTS = () => {
      textToSpeechManager.stop();
    };
    // initiate the event handler
    window.addEventListener('beforeunload', stopTTS);

    // this will clean up the event every time the component is re-rendered
    return () => {
      window.removeEventListener('beforeunload', stopTTS);
    };
  }, []);

  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const videoParam = params.get('video') ?? undefined;
  const rowParam = params.get('row') ?? undefined;
  const isDiscoveryRowVideo = params.get('isDiscoveryRowVideo') === 'true';
  const autoplay = params.get('autoplay') === 'true';
  const [prevVideo, prevRow] = usePrevVideo();
  const [nextVideo, nextRow] = useNextVideo();
  const [fallbackVideo] = useFallbackVideo();

  const onPrevVideo = () => {
    if (prevVideo && prevRow) {
      const searchParam = new URLSearchParams(location.search);
      searchParam.set('video', prevVideo.id);
      searchParam.set('row', prevRow.id);

      history.replace({
        pathname: history.location.pathname,
        search: searchParam.toString(),
      });
    }
  };

  const onNextVideo = (isAutoplay = false) => {
    if (nextVideo) {
      const searchParam = new URLSearchParams(location.search);
      searchParam.set('video', nextVideo.id);
      searchParam.set('row', nextRow?.id as unknown as string);

      if (isAutoplay) {
        searchParam.set('autoplay', isAutoplay as unknown as string);
      }

      history.replace({
        pathname: history.location.pathname,
        search: searchParam.toString(),
      });
    }
  };

  const onVideoCompleted = () => {
    dispatch(clearVideoIsClicked());
    onNextVideo(true);
  };

  const handleError = (err: unknown) => {
    const error = err as { type: string; providers?: string[]; isCarousel?: boolean; code?: string };
    let path;
    let redirect;
    switch (error.type) {
      case 'authN':
        path = {
          pathname: '/shortCode',
          search: `?providers=${error.providers?.join(',')}`,
        };
        redirect = {
          redirectTo: location,
        };
        if (error.isCarousel) {
          history.push(path, redirect);
          break;
        }

        history.replace(path, redirect);
        break;
      case 'upcoming':
        path = {
          pathname: `/upcoming/${videoParam}`,
        };
        redirect = {
          redirectTo: location,
        };

        if (error.isCarousel) {
          history.push(path, redirect);
          break;
        }

        history.replace(path, redirect);
        break;
      case 'authZ': {
        history.replace({
          pathname: '/error/authz',
        });
        break;
      }
      case 'DRM':
      case 'playback': {
        history.replace({
          pathname: '/error/playbackStartup',
        });
        break;
      }
      case 'syncbak': {
        history.replace({
          pathname: '/error/syncbak',
          search: `?code=${error.code}`,
        });
        break;
      }
      case 'network': {
        history.push({
          pathname: '/error/network',
        });
        break;
      }
      default:
        break;
    }
  };

  const ksFetcherRef = useRef<{ retry: () => Promise<unknown> }>(null);

  return (
    <AppActionsHandler>
      <LoggerOverlay>
        <AriaTextToSpeech />
        <KitchenSinkFetcher
          onKSStartupError={() => {
            history.replace({
              pathname: '/error/connection',
            });
          }}
          ref={ksFetcherRef}
        />
        <SplashWrapper>
          <div className={classNames('h-100', 'w-100', 'app-content', 'text-white')}>
            <Switch>
              <PlayerStateProvider>
                <Route path="/">
                  <AuthProvider>
                    <FeatureFlagProvider>
                      <Switch>
                      <Route path="/error/authz">
                        <ErrorPage message={<FormattedMessage id="error.message.authZ" />} />
                      </Route>
                      <Route path="/error/network">
                        <NetworkErrorPage />
                      </Route>
                      <Route path="/error/syncbak">
                        <SyncbakErrorPage />
                      </Route>
                      <Route path="/error/playbackStartup">
                        <ErrorPage
                          message={<FormattedMessage id="error.message.playbackStartup" />}
                          onDismiss={() => {
                            const searchParam = new URLSearchParams();
                            searchParam.set('video', fallbackVideo!.id as unknown as string);
                            history.replace({
                              pathname: '/',
                              search: searchParam.toString(),
                            });
                          }}
                        />
                      </Route>
                      <Route path="/error">
                        <ErrorPage message={<FormattedMessage id="error.message.default" />} />
                      </Route>
                      <Route path="/upcoming/:contentId">
                        <UpcomingEventPage />
                      </Route>
                      <Route path="/upgradeToWatch">
                        <UpgradeToWatch videoId={videoParam ?? undefined} rowId={rowParam ?? undefined} />
                      </Route>
                      <Route path="/shortCode">
                        <ShortCode />
                      </Route>
                      <Route path="/logout">
                        <Logout />
                      </Route>
                      <Route path="/legal">
                        <LegalNoticesPage />
                      </Route>
                      <Route path="/privacyUS">
                        <PrivacyPageUS />
                      </Route>
                      <Route path="/privacyGDPR">
                        <PrivacyPageGDPR />
                      </Route>
                      <Route path="/closedCaptions">
                        <ClosedCaptionsPage />
                      </Route>
                      <Route path="/contactUs">
                        <ContactUsPage />
                      </Route>
                      <Route path="/settings">
                        <SettingsPage pageName="Settings" pageType="custom" />
                      </Route>
                      <Route path="/sport/:contentId">
                        <InteriorPage pageName="sports" pageType="sport" />
                      </Route>
                      <Route path="/league/:contentId">
                        <InteriorPage pageName="leagues" pageType="league" />
                      </Route>
                      <Route path="/show/:contentId">
                        <InteriorPage pageName="shows" pageType="show" />
                      </Route>
                      <Route path="/market/:contentId">
                        <InteriorPage pageName="markets" pageType="market" />
                      </Route>
                      <Route path="/tagrow/:contentId">
                        <LandingPage pageType="tagrow" />
                      </Route>
                      <Route path="/graphql-poc">
                        <GraphQLPocRoutes />
                      </Route>
                      <Route path="/playback">
                        <PlaybackPage
                          videoId={videoParam}
                          rowId={rowParam}
                          isAutoplay={autoplay}
                          onVideoCompleted={onVideoCompleted}
                          onError={handleError}
                          onPrevVideo={onPrevVideo}
                          onNextVideo={onNextVideo}
                          isDiscoveryRowVideo={isDiscoveryRowVideo}
                        />
                      </Route>
                      <Route path="/">
                        <Home onError={handleError} />
                      </Route>
                      </Switch>
                    </FeatureFlagProvider>
                  </AuthProvider>
                </Route>
                <Route path="/error/connection">
                  <ErrorPage
                    message={<FormattedMessage id="error.message.connection" />}
                    onDismiss={async () => {
                      const results = await ksFetcherRef.current?.retry();
                      if (results) {
                        history.replace({ pathname: '/' });
                      }
                    }}
                  />
                </Route>
              </PlayerStateProvider>
            </Switch>
          </div>
          <ExitDialog />
          <FMSFetcher />
        </SplashWrapper>
      </LoggerOverlay>
    </AppActionsHandler>
  );
};

export default App;
